import { supabase } from '../lib/supabase';

export const emailService = {
  async sendQuoteCreatedEmail(quote, clientEmail, clientName, serviceTitle) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'quote_created',
          to: clientEmail,
          data: {
            quoteNumber: quote?.quoteNumber,
            clientName: clientName,
            serviceTitle: serviceTitle,
            totalAmount: quote?.totalAmount,
            currency: quote?.currency || 'USD',
            status: quote?.status,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending quote created email:', error);
      throw error;
    }
  },

  async sendQuoteSentEmail(quote, clientEmail, clientName, serviceTitle) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'quote_sent',
          to: clientEmail,
          data: {
            quoteNumber: quote?.quoteNumber,
            clientName: clientName,
            serviceTitle: serviceTitle,
            totalAmount: quote?.totalAmount,
            currency: quote?.currency || 'USD',
            expiresAt: quote?.expiresAt,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending quote sent email:', error);
      throw error;
    }
  },

  async sendQuoteAcceptedEmail(quote, clientEmail, clientName, serviceTitle) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'quote_accepted',
          to: clientEmail,
          data: {
            quoteNumber: quote?.quoteNumber,
            clientName: clientName,
            serviceTitle: serviceTitle,
            totalAmount: quote?.totalAmount,
            currency: quote?.currency || 'USD',
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending quote accepted email:', error);
      throw error;
    }
  },

  async sendQuoteRequestConfirmation(request) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'quote_request_confirmation',
          to: request?.customerEmail,
          data: {
            customerName: request?.customerName,
            requestNumber: request?.requestNumber,
            requestType: request?.requestType,
            totalAmount: request?.estimatedAmount,
            currency: request?.currency || 'USD',
            message: request?.message,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending quote request confirmation email:', error);
      throw error;
    }
  },

  async sendQuoteRequestAccepted(request) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'quote_request_accepted',
          to: request?.customerEmail,
          data: {
            customerName: request?.customerName,
            requestNumber: request?.requestNumber,
            requestType: request?.requestType,
            totalAmount: request?.estimatedAmount,
            currency: request?.currency || 'USD',
            adminNotes: request?.adminNotes,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending quote request accepted email:', error);
      throw error;
    }
  },

  async sendQuoteRequestRejected(request) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'quote_request_rejected',
          to: request?.customerEmail,
          data: {
            customerName: request?.customerName,
            requestNumber: request?.requestNumber,
            requestType: request?.requestType,
            adminNotes: request?.adminNotes,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending quote request rejected email:', error);
      throw error;
    }
  },

  async sendProjectCreatedEmail(project, clientEmail, clientName) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'project_created',
          to: clientEmail,
          data: {
            projectNumber: project?.projectNumber,
            clientName: clientName,
            title: project?.title,
            startDate: project?.startDate,
            estimatedCompletion: project?.estimatedCompletion,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending project created email:', error);
      throw error;
    }
  },

  async sendProjectUpdateEmail(project, clientEmail, clientName, updateMessage = '') {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'project_update',
          to: clientEmail,
          data: {
            projectNumber: project?.projectNumber,
            clientName: clientName,
            title: project?.title,
            status: project?.status,
            progressPercentage: project?.progressPercentage || 0,
            updateMessage: updateMessage,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending project update email:', error);
      throw error;
    }
  },

  async sendProjectCompletedEmail(project, clientEmail, clientName) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'project_completed',
          to: clientEmail,
          data: {
            projectNumber: project?.projectNumber,
            clientName: clientName,
            title: project?.title,
            actualCompletion: project?.actualCompletion,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending project completed email:', error);
      throw error;
    }
  },

  async sendMilestoneCompletedEmail(project, milestone, clientEmail, clientName) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'milestone_completed',
          to: clientEmail,
          data: {
            projectNumber: project?.projectNumber,
            clientName: clientName,
            milestoneTitle: milestone?.title,
            milestoneDescription: milestone?.description,
            progressPercentage: project?.progressPercentage || 0,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending milestone completed email:', error);
      throw error;
    }
  },

  async sendPaymentReceiptEmail(order) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'payment_receipt',
          to: order?.customerEmail,
          data: {
            orderNumber: order?.orderNumber,
            customerName: order?.customerName,
            totalAmount: order?.totalAmount,
            currency: order?.currency || 'USD',
            paymentMethod: order?.metadata?.paymentMethod,
            transactionId: order?.paymentIntentId,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending payment receipt email:', error);
      throw error;
    }
  },

  async sendFollowUpReminder(clientEmail, clientName, subject, message, quoteNumber = null, projectNumber = null) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'follow_up_reminder',
          to: clientEmail,
          data: {
            clientName: clientName,
            subject: subject,
            message: message,
            quoteNumber: quoteNumber,
            projectNumber: projectNumber,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending follow-up reminder:', error);
      throw error;
    }
  },

  // New: Inventory Alert Email
  async sendInventoryAlertEmail(alert, recipientEmail, recipientName) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'inventory_alert',
          to: recipientEmail,
          data: {
            recipientName: recipientName,
            itemName: alert?.itemName,
            itemCode: alert?.itemCode,
            currentQuantity: alert?.currentQuantity,
            minimumQuantity: alert?.minimumQuantity,
            alertType: alert?.alertType,
            location: alert?.location,
            supplierName: alert?.supplierName,
          },
        },
      });

      if (error) throw error;

      // Log notification
      await this.logNotification({
        notificationType: 'inventory_alert',
        recipientEmail: recipientEmail,
        recipientName: recipientName,
        subject: `Alerte Stock - ${alert?.itemName}`,
        message: alert?.message,
        status: 'sent',
        referenceId: alert?.id,
        referenceType: 'inventory_alert',
      });

      return data;
    } catch (error) {
      console.error('Error sending inventory alert email:', error);

      // Log failed notification
      await this.logNotification({
        notificationType: 'inventory_alert',
        recipientEmail: recipientEmail,
        recipientName: recipientName,
        subject: `Alerte Stock - ${alert?.itemName}`,
        message: alert?.message,
        status: 'failed',
        errorMessage: error?.message,
        referenceId: alert?.id,
        referenceType: 'inventory_alert',
      });

      throw error;
    }
  },

  // New: Schedule Assignment Email
  async sendScheduleAssignmentEmail(assignment, recipientEmail, recipientName, vehicleName = null) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'schedule_assignment',
          to: recipientEmail,
          data: {
            recipientName: recipientName,
            assignmentTitle: assignment?.assignmentTitle || assignment?.title,
            description: assignment?.description,
            clientName: assignment?.clientName || assignment?.client_name,
            location: assignment?.location,
            startTime: assignment?.startTime || assignment?.start_time,
            endTime: assignment?.endTime || assignment?.end_time,
            priority: assignment?.priority,
            vehicleAssigned: vehicleName,
            requiredMaterials: assignment?.requiredMaterials || assignment?.required_materials || [],
          },
        },
      });

      if (error) throw error;

      // Log notification
      await this.logNotification({
        notificationType: 'schedule_assignment',
        recipientEmail: recipientEmail,
        recipientName: recipientName,
        subject: `Nouvelle Mission - ${assignment?.assignmentTitle || assignment?.title}`,
        message: `Mission assignée: ${assignment?.assignmentTitle || assignment?.title}`,
        status: 'sent',
        referenceId: assignment?.id,
        referenceType: 'team_assignment',
      });

      return data;
    } catch (error) {
      console.error('Error sending schedule assignment email:', error);

      // Log failed notification
      await this.logNotification({
        notificationType: 'schedule_assignment',
        recipientEmail: recipientEmail,
        recipientName: recipientName,
        subject: `Nouvelle Mission - ${assignment?.assignmentTitle || assignment?.title}`,
        message: `Mission assignée: ${assignment?.assignmentTitle || assignment?.title}`,
        status: 'failed',
        errorMessage: error?.message,
        referenceId: assignment?.id,
        referenceType: 'team_assignment',
      });

      throw error;
    }
  },

  // New: Schedule Reminder Email
  async sendScheduleReminderEmail(schedule, recipientEmail, recipientName) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'schedule_reminder',
          to: recipientEmail,
          data: {
            recipientName: recipientName,
            assignmentTitle: schedule?.assignmentTitle,
            clientName: schedule?.clientName,
            location: schedule?.location,
            startTime: schedule?.startTime,
            priority: schedule?.priority,
          },
        },
      });

      if (error) throw error;

      // Log notification
      await this.logNotification({
        notificationType: 'schedule_reminder',
        recipientEmail: recipientEmail,
        recipientName: recipientName,
        subject: `Rappel Mission - ${schedule?.assignmentTitle}`,
        message: `Rappel: Mission prévue pour ${new Date(schedule?.startTime)?.toLocaleString('fr-FR')}`,
        status: 'sent',
        referenceId: schedule?.id,
        referenceType: 'schedule',
      });

      return data;
    } catch (error) {
      console.error('Error sending schedule reminder email:', error);

      // Log failed notification
      await this.logNotification({
        notificationType: 'schedule_reminder',
        recipientEmail: recipientEmail,
        recipientName: recipientName,
        subject: `Rappel Mission - ${schedule?.assignmentTitle}`,
        message: `Rappel: Mission prévue pour ${new Date(schedule?.startTime)?.toLocaleString('fr-FR')}`,
        status: 'failed',
        errorMessage: error?.message,
        referenceId: schedule?.id,
        referenceType: 'schedule',
      });

      throw error;
    }
  },

  // New: Contact Form Email
  async sendContactFormEmail(formData) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-email', {
        body: {
          emailType: 'contact_form',
          to: 'admin@orazek.com', // Replace with actual admin email or env var if possible, but hardcoding for now as verified sender is usually required
          replyTo: formData?.email,
          data: {
            name: `${formData?.firstName} ${formData?.lastName}`,
            email: formData?.email,
            phone: formData?.phone,
            subject: formData?.subject,
            message: formData?.message,
            category: formData?.serviceCategory,
            urgency: formData?.urgency,
            preferredContact: formData?.preferredContact,
            company: formData?.company,
          },
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending contact form email:', error);
      throw error;
    }
  },

  // New: Log notification to database
  async logNotification(notificationData) {
    try {
      const { data, error } = await supabase
        ?.from('notification_logs')
        ?.insert({
          notification_type: notificationData?.notificationType,
          recipient_email: notificationData?.recipientEmail,
          recipient_name: notificationData?.recipientName,
          subject: notificationData?.subject,
          message: notificationData?.message,
          status: notificationData?.status,
          sent_at: notificationData?.status === 'sent' ? new Date()?.toISOString() : null,
          error_message: notificationData?.errorMessage,
          reference_id: notificationData?.referenceId,
          reference_type: notificationData?.referenceType,
          metadata: notificationData?.metadata || {},
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error logging notification:', error);
      // Don't throw - logging failure shouldn't break the main flow
    }
  },
};